import React, { useCallback, useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { useToast } from "@hooks/useToast";
import useTranslation from "src/translations/useTranslation";
import { useAuth } from "@hooks/useAuth";
import NewDataTable, { DataTableActions, DataTableButtons, DataTableColumns } from "@molecules/NewDataTable";
import {
  DataTableMessages,
  GlobalMessages,
  LocationsModuleMessages, ManagementMessages, MenuMessages,
  ToastMessages
} from "@shared/languages/interfaces";
import ReactDOMServer from "react-dom/server";
import { GroupsModuleMessages } from "@shared/languages/interfaces/groupsModuleMessages";
import utils from "@utils/useful-functions";
import api from "@services/api";
import Header from "@components/Header";
import { ScreenPlatform } from "@store/ducks/Screen/screen.type";
import Loading from "react-fullscreen-loading";
import DialogConfirmAction from "@components/Dialog/ConfirmAction";
import {
  Button,
  Dialog, DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid
} from "@material-ui/core";
import ButtonTable from "@components/Button/ButtonTable";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import DeleteForeverIcon from "@material-ui/icons/DeleteForever";
import EditIcon from "@material-ui/icons/Edit";
import { Form } from "@unform/web";
import { Checkbox, TextField } from "unform-material-ui";
import { FormHandles } from "@unform/core";
import * as dateFns from "date-fns";
import * as Yup from "yup";
import { Group } from "@shared/interfaces/group.interface";
import getValidationErrors from "@hooks/getValidationErrors";
import { Container, ContainerModalFormGroup } from "./styles";

const Groups: React.FC = () => {

  /* endregion Refs */
  /* region States */
  const [groups, setGroups] = useState<Group[]>([] as Array<Group>);

  const [openDialogConfirmDelete, setOpenDialogConfirmDelete] = useState(false);
  const [openModalFormGroup, setOpenModalFormGroup] = useState(false);
  const [loadingGroupsList, setLoadingGroupList] = useState(false);
  const [groupDetails, setGroupDetails] = useState<Group>({} as Group);
  const [modalType, setModalType] = useState<"details" | "update" | "register">("details");
  const [activeCheckbox, setActiveCheckbox] = useState<boolean>(true);
  const [table, setTable] = useState<DataTables.Api>({} as DataTables.Api);
  const [loadingValidateFieldCode, setLoadingValidateFieldCode] = useState(false);
  const [tableClickedRow, setTableClickedRow] = useState<JQuery<HTMLTableRowElement>>({} as JQuery<HTMLTableRowElement>);
  /* endregion States */
  /* region Hooks */
  const { screen } = useSelector((screen) => screen);
  const { addToast } = useToast();
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  /* endregion Hooks */
  /* region Form Validation */
  const formRef = useRef<FormHandles>(null);
  /** Validations of unique fields
   */
  const validations = {

    validateFieldError: (fieldName: string) => {
      if (formRef.current?.getFieldError(fieldName)?.length) formRef.current?.setFieldError(fieldName, "");
    },
    validateCode: async (code: string) => {

      try {
        setLoadingValidateFieldCode(true);
        const foundGroup = groups.find((group) => group.id_group === code);

        if (foundGroup) {
          const error = new Error("Groups Code already exists");

          formRef.current?.setErrors(getValidationErrors(error));
        }
      } finally {
        setLoadingValidateFieldCode(false);
      }

    },
    validateForm: async (formData: Group, action: "details" | "register" | "update") => {

      try {
        // Define the validation types
        const schema = Yup.object().shape({
          name: Yup.string().trim().required(t(ManagementMessages.formRequiredName)),
          code: Yup.string().trim().required()
        });

        // Validate inputs
        await schema.validate(formData, { abortEarly: false });
        // Register or update driver (According action selected)
        if (action === "register") await createGroup(formData);
        else await updateGroup(formData);

      } catch (error) {
        formRef.current?.setErrors(getValidationErrors(error));
      }
    }
  };
  /* endregion Form Validation */
  /* region Constants */
  const dataTableSettings: DataTables.Settings = {
    order: [[0, "asc"]],
    columnDefs: [{ className: "dt-center", targets: -1 }]
  };
  const dataTableActions: DataTableActions[] = [
    {
      ref: ".modules-group-list-details",
      callback: (rowData: Group) => handleGroupDetails(rowData)
    },
    {
      ref: ".modules-group-list-delete",
      callback: (rowData: Group) => handleGroupDelete(rowData)
    }
  ];
  const dataTableColumns: DataTableColumns[] = [
    { // Nome
      title: t(GroupsModuleMessages.name),
      data: (group: Group) => group.name || t(GroupsModuleMessages.noNameRegistered),
      filterable: true,
      defaultContent: "",
      propertyName: "name"
    },
    { // Id Externo
      title: t(GroupsModuleMessages.externalId),
      data: (group: Group) => group.external_id,
      filterable: true,
      defaultContent: "",
      propertyName: "number"
    },
    { // Data de Registro
      title: t(GroupsModuleMessages.registrationDate),
      data: (group: Group) => group.registration_date,
      filterable: true,
      defaultContent: "",
      propertyName: "registrationDate",
      render: (data) => (data ? (dateFns.format(new Date(data), "dd/MM/yyyy HH:mm")) : "")
    },
    { // Data Atualização
      title: t(GroupsModuleMessages.updatedDate),
      data: (group: Group) => group.updated_date,
      filterable: true,
      defaultContent: "",
      propertyName: "updatedDate",
      render: (data) => (data ? (dateFns.format(new Date(data), "dd/MM/yyyy HH:mm")) : "")
    },
    { // Ativo
      title: "Status",
      filterable: true,
      propertyName: "active",
      data: (group: Group) => (
        ReactDOMServer.renderToString(
          <b style={{ color: group.active ? "rgb(70, 193, 125)" : "rgb(230, 74, 25)" }}>
            {group.active ? t(GroupsModuleMessages.active).toUpperCase() : t(GroupsModuleMessages.inactive).toUpperCase()}
          </b>
        )
      )
    },
    { // Ações
      title: t(DataTableMessages.actions),
      orderable: false,
      searchable: false,
      data: () => ReactDOMServer.renderToString(
        <Grid container spacing={1}>
          <Grid item xs sm md lg xl>
            <ButtonTable className="action-button modules-group-list-details"><FormatListBulletedIcon /></ButtonTable>
          </Grid>
          <Grid item xs sm md lg xl>
            <ButtonTable className="action-button modules-group-list-delete"><DeleteForeverIcon /></ButtonTable>
          </Grid>
        </Grid>
      ),
      width: "130px",
      filterable: false
    }
  ];
  const dataTableButtons: DataTableButtons[] = [
    {
      name: t(DataTableMessages.buttonsAddNew),
      key: "add",
      callback: () => FhandleGroupCreate()
    },
    {
      name: t(DataTableMessages.buttonsRefresh),
      key: "refresh",
      callback: () => readGroups()
    },
    {
      name: t(DataTableMessages.buttonsPrint),
      key: "print",
      callback: () => utils.clickButtonDomElement("button-print"),
      extend: "print",
      className: "button-print",
      exportOptions: {
        columns: "th:not(:last-child)"
      }
    },
    {
      name: t(DataTableMessages.buttonsExport),
      callback: () => utils.clickButtonDomElement("button-export"),
      extend: "csv",
      key: "export",
      fieldSeparator: ";",
      className: "button-export",
      filename: `relatorio_locais_${new Date().toISOString().split("T")[0]}`,
      exportOptions: {
        columns: "th:not(:last-child)"
      }
    }
  ];
  /* endregion Constants */
  // Actions on close/open modal/dialog

  // Actions close dialogs and modals
  const handleCloseModalForm = useCallback(() => {

    setGroupDetails({} as Group);

    setOpenModalFormGroup(false);

  }, []);
  /* region Functions */
  // Actions groups list
  const handleGroupDetails = useCallback((group: Group) => {

    setGroupDetails(group);
    setActiveCheckbox(group.active ?? false);

    setModalType("details");

    setOpenModalFormGroup(true);

  }, []);
  const handleGroupDelete = useCallback((group: Group) => {

    setGroupDetails(group);

    setOpenDialogConfirmDelete(true);

  }, []);
  /*
   * Open modal to create new location
   * */
  const FhandleGroupCreate = () => {
    setModalType("register");
    setOpenModalFormGroup(true);
  };

  const createGroup = useCallback(async (group: Group) => {

    try {
      let insertGroup = { ...group };

      // Remove properties without value
      Object.keys(insertGroup).forEach((key) => {
        if (insertGroup[key].length <= 0) delete insertGroup[key];
      });

      if (insertGroup.active) insertGroup.active = activeCheckbox;

      setLoadingGroupList(true);
      const { data } = await api.post("groups/create", insertGroup);

      if (data.status === "success") {

        handleCloseModalForm(); // Close modal
        addToast({ type: "success", title: t(ToastMessages.success), description: data.message });

        insertGroup = data.result;

        if (!groups.length) readGroups();
        else {

          // Add inserted group in the table
          table.row.add(insertGroup);
          table.draw();
        }

        // Search the inserted user and list
        table.search(group.name).draw();

      } else addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });

    } catch (error) {

      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });

    } finally {
      setLoadingGroupList(false);
    }
  }, [activeCheckbox, setLoadingGroupList, table]);

  /** Update a group
   * @param group Group to update
   */
  const updateGroup = useCallback(async (group: Group) => {

    try {

      const updateGroup = { ...group };

      if (group?.external_id === "") delete updateGroup.external_id;

      // Set Ibutton active option
      /* if (updateGroup.id) updateDriver.hardware = activeOptionIbutton;
       */

      // Update a driver
      setLoadingGroupList(true);
      const { data } = await api.patch(`groups/update/${groupDetails.id_group}`, updateGroup);

      if (data.status === "success") {

        handleCloseModalForm(); // Close modal
        addToast({ type: "success", title: t(ToastMessages.success), description: data.message });

        setGroupDetails(Object.assign(groupDetails, updateGroup));

        // Update row in table with updated driver data
        table.row(tableClickedRow).data(groupDetails);
        table.draw();

      } else addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });

    } catch (error) {

      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });

    } finally {
      setLoadingGroupList(false);
    }

  }, [addToast, table, tableClickedRow, groupDetails, handleCloseModalForm, t]);

  const handleCloseDialogConfirmDelete = useCallback(() => {
    setOpenDialogConfirmDelete(false);
  }, []);
  /** Check what modal type to show corresponding values and actions
   * @param detailValue
   * @param updateValue
   * @param registerValue
   */
  const defineValueAccordingModalType = useCallback((detailValue, updateValue, registerValue) => {

    switch (modalType) {
    case "details": return detailValue;
    case "update": return updateValue;
    case "register": return registerValue;
    default: return "S/N";
    }
  }, [modalType]);

  /** Get all Groups data
   */
  const readGroups = useCallback(async () => {

    try {

      // Get the travels of selected vehicle
      setLoadingGroupList(true);
      const { data } = await api.get("groups/read");

      if (data.status === "success") setGroups(data.result);
      else addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });

    } catch (error) {

      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });

    } finally {
      setLoadingGroupList(false);
    }

  }, [addToast, t]);
  /** Delete selected Group
   * @param group Group to delete
   */
  const deleteGroup = useCallback(async (group) => {

    try {

      setLoadingGroupList(true);
      const { data } = await api.delete(`groups/delete/${group.id_group}`);

      if (data.status === "success") {

        handleCloseDialogConfirmDelete(); // Close dialog confirm delete
        addToast({ type: "success", title: t(ToastMessages.success), description: data.message });

        // Removes the deleted record from the table (WE NEED THE REFERENCE OF ROW)
        table.row(tableClickedRow).remove();
        table.search("");
        table.draw();

      } else addToast({ type: "info", title: t(ToastMessages.alert), description: data.message });

    } catch (error) {

      if (!error.response) addToast({ type: "error", title: t(ToastMessages.error), description: t(ToastMessages.connectionNotEstablished) });
      else addToast({ type: "error", title: error.response.data.backend, description: error.response.data.message });

    } finally {
      setLoadingGroupList(false);
    }

  }, [addToast, handleCloseDialogConfirmDelete, table, tableClickedRow, t]);

  /* endregion Functions */
  /* region Effects */
  // Used to disable and change text when is searching for data
  useEffect(() => {
    const btnPesquisar = document.querySelector(".btn-pesquisar") as HTMLElement;

    if (btnPesquisar) {
      btnPesquisar.innerText = loadingGroupsList ? t(LocationsModuleMessages.searching) : t(GlobalMessages.search);

      if (loadingGroupsList) {
        btnPesquisar.setAttribute("disabled", "");
      } else {
        btnPesquisar.removeAttribute("disabled");
      }
    }
  }, [loadingGroupsList, t]);
  useEffect(() => {
    readGroups().then();
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [i18n.language]);

  /* endregion Effects */
  return (
    <>
      <Header title={t(MenuMessages.groups)} />
      <Container platform={screen.platform as ScreenPlatform} className="page">
        <Loading loading={loadingGroupsList} />
        {
          !loadingGroupsList && (
            <NewDataTable
              title={t(LocationsModuleMessages.dataTableTitle)}
              filters
              data={groups}
              columns={dataTableColumns}
              actions={dataTableActions}
              settings={dataTableSettings}
              buttons={dataTableButtons}
              returnTable={(table) => setTable(table)}
              returnClickedRow={(clickedRow) => setTableClickedRow(clickedRow)}
            />
          )
        }
        <DialogConfirmAction
          open={openDialogConfirmDelete}
          onClose={handleCloseDialogConfirmDelete}
          title={t(ManagementMessages.deleteConfirmationTitle)}
          actions={[
            { text: t(GlobalMessages.no), action: () => handleCloseDialogConfirmDelete() },
            { text: t(GlobalMessages.yes), action: () => deleteGroup(groupDetails) }
          ]}
        >
          {t(GroupsModuleMessages.deleteConfirmationText)} <br /><br />
          {t(ManagementMessages.dataTableName)}: {groupDetails.name} <br />
          {t(ManagementMessages.dataTableCode)}: {groupDetails.code}?
        </DialogConfirmAction>
        <ContainerModalFormGroup id="modalFormGroup">
          <Dialog
            open={openModalFormGroup}
            onClose={handleCloseModalForm}
            scroll="paper"
            container={document.getElementById("modalFormGroup")}
          >
            <DialogTitle className="mHeader">
              <div className="content">
                <div className="title">{defineValueAccordingModalType(groupDetails.name, groupDetails.name,
                  t(MenuMessages.groups))}
                </div>
              </div>
              <div className="actions">
                {modalType === "details"
                  && (
                    <Button disableRipple onClick={() => setModalType("update")}><EditIcon /></Button>
                  )}
              </div>
            </DialogTitle>
            <DialogContent dividers className="mContent">
              <Form className="form" ref={formRef} onSubmit={(formData) => validations.validateForm(formData, modalType)}>
                <DialogContentText tabIndex={-1} component="div">
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={12} lg={12}>
                      <TextField
                        className="name"
                        label={t(GroupsModuleMessages.name)}
                        margin="dense"
                        variant="outlined"
                        name="name"
                        defaultValue={groupDetails.name}
                        InputLabelProps={{ shrink: formRef.current?.getFieldValue("name").length > 0 ? true : undefined }}
                        disabled={modalType === "details"}
                        helperText={t(GlobalMessages.required)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={6}>
                      <TextField
                        className="cod test"
                        label={t(GroupsModuleMessages.code)}
                        margin="dense"
                        variant="outlined"
                        name="code"
                        defaultValue={groupDetails.code}
                        InputLabelProps={{ shrink: formRef.current?.getFieldValue("code").length > 0 ? true : undefined }}
                        disabled={modalType === "details"}
                        helperText={t(GlobalMessages.required)}
                      />
                    </Grid>
                    <Grid item xs={12} md={6} lg={6}>
                      <TextField
                        className="r-id"
                        label={t(GroupsModuleMessages.externalId)}
                        margin="dense"
                        variant="outlined"
                        name="external_id"
                        defaultValue={groupDetails.external_id}
                        InputLabelProps={{ shrink: formRef.current?.getFieldValue("external_id").length > 0 ? true : undefined }}
                        disabled={modalType === "details" || !user.super_admin}
                      />
                    </Grid>
                  </Grid>
                  <Grid item xs={12} md={12} lg={12} className="checkboxes">
                    <FormControlLabel
                      className="r-checkbox"
                      control={(
                        <Checkbox
                          onChange={(event) => setActiveCheckbox(event.target.checked)}
                          checked={activeCheckbox}
                          name="active"
                          disabled={modalType === "details"}
                        />
                      )}
                      label={t(GroupsModuleMessages.active)}
                    />
                  </Grid>
                </DialogContentText>
              </Form>
            </DialogContent>
            <DialogActions className="mFooter">
              <div className="actions">
                {
                  modalType === "details"
                    ? <></>
                    : modalType === "register"
                      // eslint-disable-next-line max-len
                      ? <Button disableRipple type="submit" color="primary" onClick={() => formRef.current?.submitForm()}>{t(GlobalMessages.register)}</Button>
                      // eslint-disable-next-line max-len
                      : <Button disableRipple type="submit" color="primary" onClick={() => formRef.current?.submitForm()}>{t(GlobalMessages.save)}</Button>
                }
              </div>
              <Button disableRipple onClick={() => handleCloseModalForm()} color="primary">{t(GlobalMessages.close)}</Button>
            </DialogActions>
          </Dialog>
        </ContainerModalFormGroup>
      </Container>
    </>
  );
};

export default Groups;
